import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import DataLoader from "../../../../components/ui/dataLoader/DataLoader";
import Accordion from "../../../../components/Accordion/Accordion";
import FormCreator from "../../../../components/Forms/FormCreator";
import Buttons from "../../../../components/ui/button/Buttons";
import { useGetL1DropdownByChannelIdMutation, useLazyGetAllDropdownForCRUDCategoryQuery, useLazyGetCategoryCriteriaCertificateMappingByIdQuery, useUpdateCategoryCriteriaCertificateMappingInformationMutation } from "../../../../app/services/Crud/crudApi";
import { setAllDropDownField } from "../../Utils/setAllDropDownField";
import { applyCertificateTemplateOptions, getCertificateTemplateOptions, hideAndShowFields, setFieldParameter } from "../../Utils/setFormUtils";
import SwalAlert from "../../../../services/swalService/SwalService";
import { useNavigate, useParams } from "react-router-dom";
import { EditSGBPCategoryData } from "./data/EditSGBPCategory.data";
import { decryptAESID } from "../../../../utils/Encryption";

const EditSGBPCategory = () => {
    const editSGBPCategoryRefs = useRef();
    const navigate = useNavigate();
    const { id } = useParams();
    const editID = useMemo(() => (id ? decryptAESID(id, false) : 0), [id]);
    const [formData, setFormData] = useState(EditSGBPCategoryData);
    const { error, success } = SwalAlert();
    const [getDropdowns, { data: dropdownData, isLoading: dropdownLoading },] = useLazyGetAllDropdownForCRUDCategoryQuery();
    const [updateMapping, { data: mappingData, isLoading: mappingLoading },] = useUpdateCategoryCriteriaCertificateMappingInformationMutation();
    const [getCategoryCriteriaCertificateMapping, { data: categoryCriteriaCertificateMappingData, isLoading: categoryCriteriaCertificateMappingLoading },] = useLazyGetCategoryCriteriaCertificateMappingByIdQuery();
    const [getL1DropdownByChannelId, { data: l1DropdownData, isLoading: l1DropdownLoading, }] = useGetL1DropdownByChannelIdMutation();

    useEffect(() => {
        getDropdowns();
        if (id) {
            getCategoryCriteriaCertificateMapping(editID);
        }
    }, [id])

    useEffect(() => {
        if (dropdownLoading || !dropdownData || !categoryCriteriaCertificateMappingData) return;

        const isStatus9 = categoryCriteriaCertificateMappingData?.statusId == 9;
        setAllDropDownField(dropdownData, isStatus9);

        // new references so FormCreator re-renders with the updated options
        setFormData((prev) => ({
            ...prev,
            formFields: prev.formFields.map((field) => ({
                ...field,
                fieldSetting: { ...field.fieldSetting },
            })),
        }));
    }, [dropdownLoading, dropdownData, categoryCriteriaCertificateMappingData]);

    useEffect(() => {
        if (!categoryCriteriaCertificateMappingLoading && categoryCriteriaCertificateMappingData) {
            setFieldParameter(categoryCriteriaCertificateMappingData);
            const channelValue = categoryCriteriaCertificateMappingData?.channelId;
            getL1DropdownByChannelId({ ChannelId: categoryCriteriaCertificateMappingData?.channelId })
            if (channelValue) {
                const channelObj = { value: channelValue };
                setFormData((prev) => {
                    let updatedForm = hideAndShowFields(prev, channelObj);
                    return {
                        ...updatedForm, initialState: { ...updatedForm.initialState, channel: channelValue, category: categoryCriteriaCertificateMappingData?.categoryL1Id, subCategory: categoryCriteriaCertificateMappingData?.subCategoryL2Id, type: categoryCriteriaCertificateMappingData?.typeL3Id, code: categoryCriteriaCertificateMappingData?.criteriaCodeId, certificateTemplate: categoryCriteriaCertificateMappingData?.certificateTemplateId, },
                    };
                });
            }
        }
    }, [categoryCriteriaCertificateMappingLoading, categoryCriteriaCertificateMappingData]);


    useEffect(() => {
        if (l1DropdownData && !l1DropdownLoading) {
            const filterDropdown = l1DropdownData?.map((item) => ({ value: item.id, label: item.name }));
            setFormData((prev) => {
                let updatedForm = { ...prev };
                updatedForm = { ...updatedForm, formFields: updatedForm.formFields.map(field => field.id === "category" ? { ...field, fieldSetting: { ...field.fieldSetting, options: filterDropdown, }, } : field), };
                return updatedForm;
            });
        }
    }, [l1DropdownData, l1DropdownLoading]);

    useEffect(() => {
        if (!dropdownData || !categoryCriteriaCertificateMappingData) return;
        const options = getCertificateTemplateOptions(dropdownData, categoryCriteriaCertificateMappingData.channelId);
        setFormData((prev) => applyCertificateTemplateOptions(prev, options));
    }, [dropdownData, categoryCriteriaCertificateMappingData]);

    useEffect(() => {
        if (!mappingLoading && mappingData) {
            if (mappingData?.errorMessage === "Criteria Mapping Already Exist") {
                error("Criteria Mapping Already Exist.");
            }
            else {
                success("Criteria mapping updated successfully.", "");
                resetForm()
                navigate('/SGBPCategory')
            }
        }
    }, [mappingLoading, mappingData]);

    const handleSubmit = () => {
        const data = editSGBPCategoryRefs?.current?.getFormData();
        if (!data) {
            error("Please fill in all mandatory fields to continue.", "")
            return;
        }
        const request = {
            SGBPCategoryCriteriaCertificateMappingId: editID,
            ChannelId: data.channel?.value || data.channel,
            CategoryL1Id: data.category?.value || data.category,
            SubCategoryL2Id: data.channel?.value == 1 || data.channel == 1 ? data.subCategory?.value || data.subCategory : null,
            TypeL3Id: data.channel?.value == 1 || data.channel == 1 ? data.type?.value || data.type : null,
            CriteriaCodeId: data.code?.value || data.code,
            CertificateTemplateId: data.certificateTemplate?.value || data.certificateTemplate,
        }
        updateMapping(request);
    }

    const handleFormFieldChange = async (dataField, value) => {
        let updatedForm = { ...formData };
        if (dataField === "channel") {
            updatedForm = hideAndShowFields(updatedForm, value);
            const apiResponse = await getL1DropdownByChannelId({ ChannelId: value?.value });
            const filterDropdown = apiResponse?.data?.map((item) => ({ value: item.id, label: item.name }));
            const categoryOptions = apiResponse?.data?.map((item) => ({ value: item.id, label: item.name })) || [];
            const templateOptions = getCertificateTemplateOptions(dropdownData, value?.value);
            // let updatedFormData = { ...updatedForm, initialState: { ...updatedForm.initialState, channel: value, category: null, subCategory: null, type: null, } };
            // updatedFormData = { ...updatedFormData, formFields: updatedFormData.formFields.map(field => field.id === "category" ? { ...field, fieldSetting: { ...field.fieldSetting, options: filterDropdown, }, } : field), };
            // setFormData(updatedFormData);
            setFormData((prev) => {
                let form = hideAndShowFields(prev, value);
                form = {
                    ...form,
                    initialState: {
                        ...form.initialState,
                        channel: value,
                        category: null,
                        subCategory: null,
                        type: null,
                        certificateTemplate: null,
                    },
                    formFields: form.formFields.map((field) =>
                        field.id === "category"
                            ? { ...field, fieldSetting: { ...field.fieldSetting, options: categoryOptions } }
                            : field
                    ),
                };
                return applyCertificateTemplateOptions(form, templateOptions);
            });
        } else {
            // update field value safely
            setFormData((prev) => ({ ...prev, initialState: { ...prev.initialState, [dataField]: value }, }));
        };
    };

    const resetForm = () => {
        const resetData = {
            ...EditSGBPCategoryData,
            initialState: {
                channel: null,
                category: null,
                subCategory: null,
                type: null,
                code: null,
                certificateTemplate: null
            },
        };
        setFormData(resetData);

    };

    const handleBack = () => {
        navigate('/SGBPCategory')
    }

    return (
        <div className="add-requirement">
            <Suspense fallback={<DataLoader />}>
                <Accordion
                    title="Edit Criteria Mapping"
                    className="mb-4"
                    button={false}
                    accordion={false}
                >
                    <div className="row">
                        {dropdownData && categoryCriteriaCertificateMappingData &&
                            <FormCreator
                                ref={editSGBPCategoryRefs}
                                {...formData}
                                onFormFieldValueChange={handleFormFieldChange}
                            />
                        }
                    </div>
                </Accordion>
                <div className="add-requirement-btn-wrapper">
                    <Buttons
                        buttonText="Back"
                        buttonTypeClassName="btn btn-secondary mb-2"
                        onClick={handleBack}
                    />
                    <div className="save-btn-wrapper">
                        <Buttons
                            buttonText="Save"
                            buttonTypeClassName="add-btn"
                            onClick={handleSubmit}
                            isDisable={categoryCriteriaCertificateMappingData?.statusId == 9}
                        />
                    </div>
                </div>

            </Suspense>
        </div>
    );
};

export default EditSGBPCategory;
